import { DialogTitle, GridProps, useTheme } from '@mui/material';
import { Checkbox, ContextForm, FileUpload, FormContext, IFormAfterSubmit, InputOnlyField, Numeric, Select, Text } from '@ngt/forms';
import { ValidateOn } from '@ngt/forms-core';
import { FC, useCallback, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { StagePricelist } from '../api/dtos';
import { FixedHeightInputOnlyField, FormSection, IFormDialogProps, StyledButton, StyledDialog, StyledDialogActions, StyledDialogContent, StyledFormColumn, StyledFormSubmitButton } from './inputs/CommonFormElements';

const COLUMN_SIZING: GridProps['size'] = {
    xs: 12,
    sm: 12,
    md: 6,
    lg: 6,
    xl: 6
} 

const SubStageSelect: FC = () => {
    const params = useParams();
    const { validating, loading} = useContext(FormContext);
    const navigate = useNavigate();


    return (
        <InputOnlyField 
            label="SubStage"
            component={Select}
            propertyPath={'subStage'}
            onChange={(_e, _c, item) => {
                const subStage = item?.id

                if (params.subStage != subStage && !validating && !loading) {

                    if (subStage) {
                        navigate(`/projects/${params.projectId}/stages/${params.stageId}/style-pricelist/${subStage}`, {replace: true});
                    }
                    else {
                        navigate(`/projects/${params.projectId}/stages/${params.stageId}/style-pricelist`, {replace: true});
                    }
                }
            }}
        />
    );
}

const StagePricelistForm: FC<IFormDialogProps> = (props) => {
    const theme = useTheme();

    const onAfterSubmit: IFormAfterSubmit<StagePricelist> = useCallback(async (_newValue, _formState, _formActions) => {
        props.onClose();
    }, [props.onClose])

    return (
        <ContextForm
            onAfterSubmit={onAfterSubmit}
            validateOn={ValidateOn.onChange}
            validateOnReset
        >
            <StyledDialog {...props}>
                <DialogTitle
                    sx={{
                        backgroundColor: theme.palette.secondary.light,
                        color: "white"
                    }}
                >
                    Style Pricelist
                </DialogTitle>
                <StyledDialogContent>
                    <FormSection>
                        <StyledFormColumn
                            size={COLUMN_SIZING}
                        >
                            <SubStageSelect />
                            <InputOnlyField
                                label="Name"
                                component={Text} 
                                propertyPath={'feasibilityPrice'}
                            />
                            <FixedHeightInputOnlyField
                                label="File Name"
                                component={FileUpload} 
                                propertyPath={'file'}
                            />
                            <InputOnlyField
                                label="Horizontal Start Position"
                                component={Numeric} 
                                propertyPath={'startX'}
                            />
                            <InputOnlyField
                                label="Vertical Start Position"
                                component={Numeric} 
                                propertyPath={'startY'}
                            />
                            <InputOnlyField 
                                label="Header Font"
                                component={Text}
                                propertyPath={'headerFont'}
                            />
                            <InputOnlyField 
                                label="Row Font"
                                component={Text}
                                propertyPath={'rowFont'}
                            />
                            <InputOnlyField 
                                label="Header Text Size"
                                component={Numeric}
                                propertyPath={'headerTextSize'}
                            />
                            <InputOnlyField 
                                label="Row Text Size"
                                component={Text}
                                propertyPath={'rowTextSize'}
                            />
                            <InputOnlyField 
                                label="Header Text Colour"
                                component={Text}
                                propertyPath={'headerTextColor'}
                            />
                            <InputOnlyField 
                                label="Row Text Colour"
                                component={Text}
                                propertyPath={'rowTextColor'}
                            />
                            <FixedHeightInputOnlyField 
                                label="Header Bold"
                                component={Checkbox}
                                propertyPath={'headerBold'}
                            />
                            <FixedHeightInputOnlyField 
                                label="Row Text Bold"
                                component={Checkbox}
                                propertyPath={'rowTextBold'}
                            />
                        </StyledFormColumn>
                        <StyledFormColumn
                            size={COLUMN_SIZING}
                        >
                            <InputOnlyField 
                                label="Header Height"
                                component={Numeric}
                                propertyPath={'headerHeight'}
                            />
                            <InputOnlyField
                                label="Row Height"
                                component={Numeric} 
                                propertyPath={'Row Height'}
                            />
                            <InputOnlyField
                                label="Max Lots"
                                component={Numeric} 
                                propertyPath={'maxLots'}
                            />
                            <InputOnlyField
                                label="Lots Per Column"
                                component={Numeric} 
                                propertyPath={'lotsPerColumn'}
                            />
                            <InputOnlyField
                                label="Header Background Colour"
                                component={Text} 
                                propertyPath={'headerBackColor'}
                            />
                            <InputOnlyField 
                                label="Row Colour"
                                component={Text}
                                propertyPath={'rowColor'}
                            />
                            <InputOnlyField 
                                label="Alt Row Colour"
                                component={Text}
                                propertyPath={'altRowColor'}
                            />
                            <FixedHeightInputOnlyField 
                                label="Show Borders"
                                component={Checkbox}
                                propertyPath={'showBorders'}
                            />
                            <InputOnlyField 
                                label="Border Colour"
                                component={Text}
                                propertyPath={'borderColor'}
                            />
                            <InputOnlyField 
                                label="Border Size"
                                component={Numeric}
                                propertyPath={'borderSize'}
                            />
                            <FixedHeightInputOnlyField 
                                label="Header Separators "
                                component={Checkbox}
                                propertyPath={'headerSeparators'}
                            />
                            <FixedHeightInputOnlyField 
                                label="Footer Border"
                                component={Checkbox}
                                propertyPath={'footerBorder'}
                            />
                        </StyledFormColumn>
                    </FormSection>
                </StyledDialogContent>
                <StyledDialogActions>
                    <StyledFormSubmitButton variant="contained">Submit</StyledFormSubmitButton>
                    <StyledButton variant="contained" onClick={props.onClose}>Cancel</StyledButton>
                </StyledDialogActions>
            </StyledDialog>
        </ContextForm>
    );
}

export default StagePricelistForm;